import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { m } from 'framer-motion';
import './Login.scss';
import { Link, Outlet, useNavigate, useOutlet } from 'react-router-dom';
import Spinner from '../Spinner';
import sue from '../../assets/img/sue1.jpg';
import { EthereumIcon } from 'components/Icons/Icons';
import { AnchorText } from 'components/AnchorText/AnchorText';
import { useUser } from '../../hooks/useUser';
import { Route, routes } from '../../routing/routes';
import { safeLocalRedirect, urlParameter } from '../../utils/url';
import { LOGIN_REDIRECT_PARAMETER } from './DusktechLogin';
import { WalletModal } from 'components/WalletModal/WalletModal';
import { deleteReferralId, getReferralId } from '../../contexts/UserReferralContext';
import { gtag } from '../../utils/analytics';
import { formatPublicAddress } from 'utils/format';

function ReferredSubtitle() {
  return (
    <p className="mt-4 text-center text-small">
      Only new accounts qualify for the referral program. Not being referred?
      <AnchorText
        href={routes.login()}
        onClick={() => {
          gtag('event', 'cancel referral');
          deleteReferralId();
        }}
      >
        Login Instead
      </AnchorText>
    </p>
  );
}

function LoginSubtitle() {
  return (
    <p className="mt-4 text-center text-small">
      Don&apos;t have a DuskBreakers account?{' '}
      <AnchorText href={routes.register()}>Register Here</AnchorText>
    </p>
  );
}

function ReferredRegisterButtonSection({ referralId }: { referralId: string }) {
  return (
    <Link
      className="btn btn-primary primary-login-btn"
      to={routes.register()}
      onClick={() => {
        gtag('event', 'login page referral button click');
      }}
    >
      <div className="referral-button-container">
        Register with Referral
        <span className="referral-button-subtitle">Code {referralId}</span>
      </div>
    </Link>
  );
}

function LoginButtonSection({ redirect }: { redirect?: Route }) {
  return (
    <Link className="btn btn-primary primary-login-btn" to={routes.loginDusktech({ redirect })}>
      Breaker ID Login
    </Link>
  );
}

const DusktechLogin = () => {
  const rawRedirect = urlParameter(LOGIN_REDIRECT_PARAMETER);
  const safeRedirect = rawRedirect ? safeLocalRedirect(rawRedirect) : undefined;
  // this should be safe to call outside of useEffect
  const referralId = getReferralId();

  return (
    <div className="text-center">
      <p className="mt-5 text-medium breaker-login-text">Enhance your experience.</p>
      {referralId ? (
        <ReferredRegisterButtonSection referralId={referralId} />
      ) : (
        <LoginButtonSection redirect={safeRedirect} />
      )}

      {referralId ? <ReferredSubtitle /> : <LoginSubtitle />}
    </div>
  );
};

function LogoutButton() {
  const { logout, account } = useUser();

  return (
    <div className="text-center">
      {account.ethereum?.address && (
        <p className="mt-5 text-medium">
          <span className="address-label-text">Public Address: </span>
          <span className="address-text">{formatPublicAddress(account.ethereum.address)}</span>
        </p>
      )}
      <button onClick={() => logout()} className="btn btn-primary primary-logout-btn">
        Disconnect Wallet
      </button>
    </div>
  );
}

function LoginButton() {
  const { moralis } = useUser();
  const { isAuthenticating } = moralis;
  const [walletModalOpen, setWalletModalOpen] = useState(false);

  return (
    <div className="d-flex align-items-center flex-column">
      <div className="mt-2 mb-4 mx-auto login-eth-icon">
        <EthereumIcon />
      </div>
      {isAuthenticating && Spinner({ color: 'white' })}
      {!isAuthenticating && (
        <button
          onClick={() => {
            setWalletModalOpen(true);
          }}
          className="btn btn-primary primary-login-btn"
        >
          Connect Wallet
        </button>
      )}

      {walletModalOpen && <WalletModal onClose={() => setWalletModalOpen(false)} />}
    </div>
  );
}

function LoginDisclaimer() {
  return (
    <p className="text-muted text-center text-medium p-0 mt-6">
      Connect your wallet to enable Breaker ID login.
    </p>
  );
}

const Login = () => {
  const outlet = useOutlet();
  const heroimg = {
    background: 'url(' + sue + ') no-repeat center center',
    backgroundSize: 'cover',
  };

  return (
    <m.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="login"
    >
      <Helmet>
        <title>Login</title>
        <meta name="description" content="Manage your DuskBreakers account here." />
      </Helmet>

      <section
        className="
        d-flex
        align-items-center
        justify-content-center
        has-bg
      "
      >
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <h1 className="mb-5">Login</h1>
            </div>
          </div>
        </div>

        <div className="is-bg fade-hero" style={heroimg} />
      </section>

      <section className="mt-n6 mt-lg-n7">
        <div className="container">
          <div className="row d-flex justify-content-center">
            <div className="col">{outlet ? <Outlet /> : <BaseLogin />}</div>
          </div>
        </div>
      </section>
    </m.main>
  );
};

function BaseLogin() {
  const { moralis, account } = useUser();
  const { isAuthenticated } = moralis;
  const navigate = useNavigate();
  const referralId = getReferralId();

  const isDusktechLoggedIn = account.dusktech?.userId;
  useEffect(() => {
    if (isDusktechLoggedIn) {
      navigate(routes.duskAccount.base());
    }
  }, [isDusktechLoggedIn, navigate]);

  const stepTwoPrompt = referralId ? 'Register' : 'Breaker ID Login';

  return (
    <div className="col mb-3">
      <p className="text-huge">Welcome, Breaker!</p>
      <p>
        In order to interact with our games and services, please connect your Ethereum wallet. To
        upgrade to an enhanced experience, please login with your Breaker. Mobile users should login
        from the MetaMask app.
      </p>

      <div className="row">
        <div className="col-md-6 mb-3 mb-md-0">
          <div className="card h-100">
            <div className="card-header login-card-header">
              <p className="p-3 m-0 text-medium text-center">
                <span className="text-muted">Step 1:</span>&nbsp; Connect Ethereum Wallet
              </p>
            </div>
            <div className="card-body login-card-body">
              {isAuthenticated ? <LogoutButton /> : <LoginButton />}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-header login-card-header">
              <p className="p-3 m-0 text-medium text-center">
                <span className="text-muted">Step 2:</span>&nbsp; {stepTwoPrompt}
              </p>
            </div>
            <div className="card-body login-card-body ">
              {isAuthenticated ? <DusktechLogin /> : <LoginDisclaimer />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
